import { baseFilters } from "./base-filters"
import {
  createStatusFilter,
  createSearchableMultiSelectFilter,
  createMultiSelectFilter,
  createSelectFilter,
  createDateFilter
} from "./filter-factories"
import { StatusRegistry } from "./status-registry"
import { commonOptions } from "./common-options"
import { PROGRAM_OPTIONS } from "@shared/constants/billing-constants"
import type { FilterConfig, FilterOption } from "@shared/types/filters"

function createEntityStatusFilter(entityType: Parameters<typeof StatusRegistry.getOptions>[0]) {
  const statusOptions = StatusRegistry.getOptions(entityType) as FilterOption[]
  return createStatusFilter("status", "Status", statusOptions)
}

const commonEntityFilters = {
  search: baseFilters.search,
  dateRange: baseFilters.dateRange,
  amount: baseFilters.amount,
  currency: baseFilters.currency,
  paymentMethod: baseFilters.paymentMethod,
  monthlyVolume: baseFilters.monthlyVolume,
}

export const entityFilters = {
  payments: [
    commonEntityFilters.dateRange,
    commonEntityFilters.amount,
    commonEntityFilters.currency,
    createEntityStatusFilter('payment'),
    commonEntityFilters.paymentMethod,
  ],

  bankPayments: [
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    commonEntityFilters.dateRange,
    commonEntityFilters.amount,
    commonEntityFilters.currency,
    createEntityStatusFilter('payment'),
    commonEntityFilters.paymentMethod,
  ],

  merchants: [
    commonEntityFilters.search,
    createEntityStatusFilter('merchant'),
    commonEntityFilters.dateRange,
    commonEntityFilters.monthlyVolume,
  ],

  refunds: [
    commonEntityFilters.search,
    createEntityStatusFilter('refund'),
    commonEntityFilters.amount,
    commonEntityFilters.currency,
    commonEntityFilters.paymentMethod,
    commonEntityFilters.dateRange,
  ],

  disputes: [
    commonEntityFilters.search,
    createEntityStatusFilter('dispute'),
    commonEntityFilters.amount,
    commonEntityFilters.dateRange,
  ],

  orders: [
    commonEntityFilters.search,
    createEntityStatusFilter('order'),
    commonEntityFilters.dateRange,
    commonEntityFilters.amount,
  ],

  customers: [
    commonEntityFilters.dateRange,
    createEntityStatusFilter('customer'),
    commonEntityFilters.amount,
    createSearchableMultiSelectFilter("customers", "Customers", "Search customers by name, email, or ID..."),
  ],

  terminals: [
    createEntityStatusFilter('terminal'),
    createSearchableMultiSelectFilter("store", "Store", "Search stores...", commonOptions.terminalStore),
    createSearchableMultiSelectFilter("location", "Location", "Search locations...", commonOptions.terminalLocation),
    createMultiSelectFilter("type", "Type", commonOptions.terminalType),
    createDateFilter("createdDate", "Created", "Select created date range"),
    createDateFilter("lastSeenDate", "Last Seen", "Select last seen range"),
    createDateFilter("blockedDate", "Blocked", "Select blocked date range"),
    createDateFilter("lastUpdatedDate", "Last updated", "Select update date range"),
  ],

  products: [
    commonEntityFilters.search,
    createEntityStatusFilter('product'),
    createMultiSelectFilter("category", "Category", commonOptions.productCategory),
    createMultiSelectFilter("type", "Type", commonOptions.productType),
  ],

  ecommerce: [
    createEntityStatusFilter('ecommerce'),
    createMultiSelectFilter("type", "Type", commonOptions.ecommerceType),
    createDateFilter("createdDate", "Created", "Select created date range"),
    createDateFilter("lastUsedDate", "Last used", "Select last used date range"),
    createDateFilter("blockedDate", "Blocked", "Select blocked date range"),
    createDateFilter("lastUpdatedDate", "Last updated", "Select last updated date range"),
  ],

  "payment-link": [
    createEntityStatusFilter('payment-link'),
    createMultiSelectFilter("type", "Type", commonOptions.paymentLinkType),
    createDateFilter("createdDate", "Created", "Select created date range"),
    createDateFilter("expiredDate", "Expired", "Select expired date range"),
    createDateFilter("lastUsedDate", "Last used", "Select last used date range"),
    createDateFilter("blockedDate", "Blocked", "Select blocked date range"),
    createDateFilter("lastUpdatedDate", "Last updated", "Select last updated date range"),
  ],

  inventory: [
    commonEntityFilters.search,
    createSelectFilter("model", "Model", []),
    createSelectFilter("store", "Store", []),
    createDateFilter("orderedDate", "Ordered", "Select date range"),
    createDateFilter("fulfilledDate", "Fulfilled", "Select date range"),
    createDateFilter("movedDate", "Moved", "Select date range"),
  ],


  invoices: [
    commonEntityFilters.search,
    createEntityStatusFilter('invoice'),
    createDateFilter("createdDate", "Created", "Select created date range"),
    createDateFilter("dueDate", "Due date", "Select due date range"),
    createDateFilter("finalizationDate", "Finalization date", "Select finalization date range"),
    commonEntityFilters.amount,
  ],

  members: [
    commonEntityFilters.search,
    createEntityStatusFilter('member'),
    commonEntityFilters.dateRange,
  ],

  settlements: [
    commonEntityFilters.search,
    createEntityStatusFilter('settlement'),
    commonEntityFilters.dateRange,
    commonEntityFilters.amount,
  ],

  digital: [
    commonEntityFilters.search,
    createEntityStatusFilter('digital'),
    commonEntityFilters.dateRange,
  ],

  "billing-schedules": [
    commonEntityFilters.search,
    createEntityStatusFilter('billing-schedule'),
    commonEntityFilters.amount,
    commonEntityFilters.dateRange,
    createMultiSelectFilter("intervalType", "Type", [
      { label: "Milestone", value: "milestone" },
      { label: "Phase", value: "phase" },
      { label: "Plan", value: "plan" },
      { label: "Custom", value: "custom" }
    ]),
  ],

  "plan": [
    commonEntityFilters.search,
    createEntityStatusFilter('plan'),
    createDateFilter("createdDate", "Created", "Select created date range"),
  ],

  "subscriptions": [
    createEntityStatusFilter('subscription'),
    createSearchableMultiSelectFilter("customers", "Customer", "Search customers by name, email, or ID..."),
    createDateFilter("createdDate", "Created", "Select created date range"),
  ],

  "pricing-table": [
    commonEntityFilters.search,
    createEntityStatusFilter('pricing-table'),
    createDateFilter("createdDate", "Created", "Select created date range"),
  ],

  "quotes": [
    createEntityStatusFilter('quote'),
    createDateFilter("createdDate", "Created", "Select created date range"),
    commonEntityFilters.amount,
  ],

  "mm-accounts": [
    createEntityStatusFilter('mm-account'),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    commonEntityFilters.currency,
    createDateFilter("createdDate", "Created", "Select created date range"),
  ],

  "mm-reserves": [
    createEntityStatusFilter('mm-reserve'),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    commonEntityFilters.currency,
    createDateFilter("createdDate", "Created", "Select created date range"),
  ],

  "mm-operations": [
    commonEntityFilters.currency,
    createMultiSelectFilter("type", "Type", [
      { value: "charge", label: "Charge" },
      { value: "refund", label: "Refund" },
      { value: "payout", label: "Payout" },
      { value: "adjustment", label: "Adjustment" },
      { value: "stripe-fee", label: "Stripe fee" },
      { value: "deposit", label: "Deposit" },
      { value: "transfer", label: "Transfer" },
      { value: "fee-collection", label: "Fee Collection" },
    ]),
    createDateFilter("createdDate", "Created", "Select created date range"),
    createDateFilter("availableOnDate", "Available on", "Select available on date range"),
  ],

  "mm-adjustments": [
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createDateFilter("createdAt", "Date", "Select date range"),
    createEntityStatusFilter('mm-adjustment'),
    createMultiSelectFilter("type", "Type", [
      { value: "credit", label: "Credit" },
      { value: "debit", label: "Debit" },
      { value: "correction", label: "Correction" },
      { value: "fee-waiver", label: "Fee Waiver" },
      { value: "chargeback", label: "Chargeback" },
    ]),
    {
      type: "amountRange",
      key: "fee",
      label: "Fee",
      min: 0,
      max: 1000000,
    } as FilterConfig,
  ],

  // ── Overview Rails ───────────────────────────────────────────────────
  'rail-alert': [
    createDateFilter("timestamp", "Date", "Select date range"),
    createMultiSelectFilter("type", "Alert Type", [
      { value: "approval_rate_drop", label: "Approval Rate Drop" },
      { value: "clearing_rejection", label: "Clearing Rejection" },
      { value: "settlement_mismatch", label: "Settlement Mismatch" },
      { value: "network_connectivity", label: "Network Connectivity" },
      { value: "duplicate_detection", label: "Duplicate Detection" },
      { value: "late_presentment", label: "Late Presentment" },
      { value: "dispute_threshold", label: "Dispute Threshold" },
    ]),
    createMultiSelectFilter("severity", "Severity", [
      { value: "critical", label: "Critical" },
      { value: "warning", label: "Warning" },
      { value: "info", label: "Info" },
    ]),
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
      { value: "both", label: "Both" },
    ]),
  ],

  'review-item': [
    createDateFilter("createdAt", "Date", "Select date range"),
    createMultiSelectFilter("type", "Type", [
      { value: "authorization", label: "Authorization" },
      { value: "clearing", label: "Clearing" },
      { value: "settlement", label: "Settlement" },
    ]),
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
    ]),
  ],

  // ── Card Rails ──────────────────────────────────────────────────────
  authorization: [
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
    ]),
    createEntityStatusFilter('authorization'),
    createDateFilter("createdDate", "Date", "Select date range"),
    commonEntityFilters.amount,
    commonEntityFilters.currency,
  ],

  clearing: [
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
    ]),
    createEntityStatusFilter('clearing'),
    createDateFilter("createdDate", "Date", "Select date range"),
    commonEntityFilters.amount,
    commonEntityFilters.currency,
  ],

  'clearing-batch': [
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
    ]),
    createEntityStatusFilter('clearing-batch'),
    createDateFilter("submittedDate", "Date", "Select date range"),
    commonEntityFilters.amount,
    commonEntityFilters.currency,
  ],

  settlement: [
    createDateFilter("createdDate", "Date", "Select date range"),
    createEntityStatusFilter('settlement'),
    commonEntityFilters.amount,
    commonEntityFilters.currency,
  ],

  "dispute-rail": [
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
    ]),
    createEntityStatusFilter('dispute-rail'),
    createDateFilter("createdDate", "Date", "Select date range"),
    commonEntityFilters.amount,
  ],

  reconciliation: [
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
    ]),
    createEntityStatusFilter('reconciliation'),
    createDateFilter("createdDate", "Date", "Select date range"),
    commonEntityFilters.currency,
  ],

  'card-settlement-transaction': [
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
      { value: "amex", label: "Amex" },
      { value: "discover", label: "Discover" },
    ]),
    createEntityStatusFilter('card-settlement-transaction'),
    createDateFilter("settlementDate", "Settlement Date", "Select date range"),
    commonEntityFilters.amount,
    commonEntityFilters.currency,
  ],

  // ── Billing Admin ────────────────────────────────────────────────────
  tariff: [
    createDateFilter("effectiveDate", "Date", "Select date range"),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createMultiSelectFilter("bank", "Bank", commonOptions.bank),
  ],

  fee: [
    createMultiSelectFilter("feeType", "Fee Type", [
      { value: 'blended', label: 'Blended' },
      { value: 'variable', label: 'Variable' },
      { value: 'fixed', label: 'Fixed' },
    ]),
    createDateFilter("effectiveDate", "Date", "Select date range"),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createMultiSelectFilter("bank", "Bank", commonOptions.bank.filter((b) => b.value !== 'all')),
    createSearchableMultiSelectFilter("tariff", "Tariff", "Search tariffs..."),
  ],

  "fx-schedule": [
    createDateFilter("effectiveDate", "Date", "Select date range"),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
  ],

  "billing-reserve": [
    createDateFilter("effectiveDate", "Date", "Select date range"),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
  ],

  "billing-payout": [
    createDateFilter("effectiveDate", "Date", "Select date range"),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
  ],

  "mm-deposits": [
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createDateFilter("createdDate", "Date", "Select date range"),
    createEntityStatusFilter('mm-deposit'),
  ],

  "mm-payouts": [
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createDateFilter("createdDate", "Date", "Select date range"),
    createEntityStatusFilter('mm-payout'),
  ],

  "network-token": [
    createDateFilter("createdDate", "Date", "Select date range"),
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
      { value: "amex", label: "Amex" },
      { value: "discover", label: "Discover" },
    ]),
    createEntityStatusFilter('network-token'),
  ],

  "uprdr-batch": [
    createDateFilter("createdDate", "Date", "Select date range"),
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
      { value: "amex", label: "Amex" },
      { value: "discover", label: "Discover" },
    ]),
    createEntityStatusFilter('uprdr-batch'),
  ],

  "native-token": [
    createDateFilter("createdDate", "Date", "Select date range"),
    createEntityStatusFilter('native-token'),
  ],

  // ── Team & Security ─────────────────────────────────────────────────
  'team-member': [
    commonEntityFilters.search,
    createEntityStatusFilter('team-member'),
    createSelectFilter("role", "Role", [
      { value: "Owner", label: "Owner" },
      { value: "Administrator", label: "Administrator" },
      { value: "Developer", label: "Developer" },
      { value: "Analyst", label: "Analyst" },
      { value: "Support specialist", label: "Support specialist" },
      { value: "View only", label: "View only" },
      { value: "Custom", label: "Custom" },
    ]),
    createDateFilter("dateAdded", "Date added", "Select date range"),
  ],

  'security-event': [
    commonEntityFilters.search,
    createEntityStatusFilter('security-event'),
    createSelectFilter("eventType", "Event type", [
      { value: "login", label: "Login" },
      { value: "logout", label: "Logout" },
      { value: "login_failed", label: "Login failed" },
      { value: "password_change", label: "Password change" },
      { value: "2fa_enabled", label: "2FA enabled" },
      { value: "2fa_disabled", label: "2FA disabled" },
      { value: "role_change", label: "Role change" },
      { value: "member_invited", label: "Member invited" },
      { value: "member_removed", label: "Member removed" },
      { value: "api_key_created", label: "API key created" },
      { value: "api_key_revoked", label: "API key revoked" },
    ]),
    createDateFilter("timestamp", "Date", "Select date range"),
  ],

  'security-policy': [
    commonEntityFilters.search,
    createEntityStatusFilter('security-policy'),
    createSelectFilter("appliesTo", "Applies to", [
      { value: "all", label: "All roles" },
      { value: "Owner", label: "Owner" },
      { value: "Administrator", label: "Administrator" },
      { value: "Developer", label: "Developer" },
      { value: "Analyst", label: "Analyst" },
      { value: "Support specialist", label: "Support specialist" },
      { value: "View only", label: "View only" },
      { value: "Custom", label: "Custom" },
    ]),
  ],

  // ── Sonar / Velocity ──────────────────────────────────────────────
  velocity: [
    createDateFilter("effectiveDate", "Date", "Select date range"),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createEntityStatusFilter('velocity'),
  ],

  'fraud-schedule': [
    createDateFilter("effectiveDate", "Date", "Select date range"),
    createMultiSelectFilter("program", "Program", PROGRAM_OPTIONS),
    createSearchableMultiSelectFilter("merchant", "Merchant", "Search merchants..."),
    createEntityStatusFilter('fraud-schedule'),
  ],

  // ── Engagement ──────────────────────────────────────────────
  enrollment: [
    commonEntityFilters.search,
    createEntityStatusFilter('enrollment'),
    createMultiSelectFilter("channel", "Channel", [
      { value: "in_store", label: "In-Store" },
      { value: "online", label: "Online" },
      { value: "app", label: "App" },
      { value: "referral", label: "Referral" },
      { value: "import", label: "Import" },
      { value: "api", label: "API" },
    ]),
    createMultiSelectFilter("tier", "Tier", [
      { value: "Bronze", label: "Bronze" },
      { value: "Silver", label: "Silver" },
      { value: "Gold", label: "Gold" },
      { value: "Platinum", label: "Platinum" },
    ]),
    createDateFilter("enrolledDate", "Enrolled", "Select enrollment date range"),
  ],

  campaign: [
    commonEntityFilters.search,
    createEntityStatusFilter('campaign'),
    createMultiSelectFilter("objective", "Objective", [
      { value: "increase_frequency", label: "Increase Frequency" },
      { value: "boost_aov", label: "Boost AOV" },
      { value: "reduce_churn", label: "Reduce Churn" },
      { value: "drive_referrals", label: "Drive Referrals" },
      { value: "new_customer_acquisition", label: "New Customer Acquisition" },
      { value: "product_adoption", label: "Product Adoption" },
      { value: "seasonal_promotion", label: "Seasonal Promotion" },
      { value: "win_back", label: "Win Back" },
      { value: "vip_retention", label: "VIP Retention" },
      { value: "cross_sell", label: "Cross-Sell" },
    ]),
    createDateFilter("startDate", "Start Date", "Select start date range"),
  ],

  // ── POS ──────────────────────────────────────────────
  "pos-menu": [
    createSearchableMultiSelectFilter("store", "Store", "Search stores...", commonOptions.terminalStore),
    createEntityStatusFilter('pos-menu'),
  ],

  "pos-inventory": [
    commonEntityFilters.search,
    createSelectFilter("category", "Category", [
      { value: "Produce", label: "Produce" },
      { value: "Dairy", label: "Dairy" },
      { value: "Meat", label: "Meat" },
      { value: "Seafood", label: "Seafood" },
      { value: "Dry Goods", label: "Dry Goods" },
      { value: "Herbs", label: "Herbs" },
      { value: "Seasoning", label: "Seasoning" },
      { value: "Oils", label: "Oils" },
      { value: "Baking", label: "Baking" },
      { value: "Beverages", label: "Beverages" },
      { value: "Pantry", label: "Pantry" },
      { value: "Packaging", label: "Packaging" },
      { value: "Cleaning", label: "Cleaning" },
    ]),
    createSelectFilter("type", "Type", [
      { value: "raw_material", label: "Ingredient" },
      { value: "finished_good", label: "Finished Product" },
      { value: "supply", label: "Supply" },
      { value: "packaging", label: "Packaging" },
      { value: "beverage", label: "Beverage" },
    ]),
  ],

  // ── Programs (Bank-only) ─────────────────────────────────────────────
  cardPrograms: [
    commonEntityFilters.search,
    createEntityStatusFilter('card-program'),
    commonEntityFilters.dateRange,
    createMultiSelectFilter("type", "Type", [
      { value: "credit", label: "Credit" },
      { value: "debit", label: "Debit" },
      { value: "prepaid", label: "Prepaid" },
      { value: "commercial", label: "Commercial" },
      { value: "virtual", label: "Virtual" },
    ]),
    createMultiSelectFilter("network", "Network", [
      { value: "visa", label: "Visa" },
      { value: "mastercard", label: "Mastercard" },
      { value: "amex", label: "Amex" },
      { value: "discover", label: "Discover" },
    ]),
  ],

  programProducts: [
    commonEntityFilters.search,
    createEntityStatusFilter('program-product'),
    commonEntityFilters.dateRange,
    createMultiSelectFilter("category", "Category", [
      { value: "consumer", label: "Consumer" },
      { value: "business", label: "Business" },
      { value: "premium", label: "Premium" },
      { value: "basic", label: "Basic" },
    ]),
  ],

} as const

export function getEntityFilters(entityType: keyof typeof entityFilters) {
  return entityFilters[entityType]
}